import * as React from 'react'

import * as c from '../../../common'
import * as r from '../../../react-utils'

import useMediaQuery from '@mui/material/useMediaQuery'
import Feather from 'react-native-vector-icons/Feather'
import { ThemeProvider, Theme, createTheme } from '@mui/material/styles'
import { useParams } from 'react-router-dom'

import * as cp from '../../comps'
import * as cpm from '../../../app/components'
import * as gs from '../../../app/gStyles'

import ReportPages, { horizontalPad } from './ReportPages'

export interface NewSummaryProps {
  isPDF: boolean
  /**
   * @see PageReportProps['noPDF']
   */
  noPDF?: boolean
}

export default React.memo<NewSummaryProps>(function NewJobSummary({
  isPDF,
  noPDF,
}) {
  const { customerID } = useParams<'customerID'>()
  if (!customerID) {
    throw new ReferenceError(
      `customerID route param not found in <NewJobSummary />, instead found: ${customerID}`,
    )
  }
  //#region styling
  const nativePrefersDark = useMediaQuery('(prefers-color-scheme: dark)')
  const innerTheme = React.useMemo(
    (): Theme =>
      createTheme({
        palette: { mode: nativePrefersDark ? 'dark' : 'light' },
        breakpoints: { values: r.MUIBreakpoints },
      }),
    [nativePrefersDark],
  )
  //#endregion styling
  //#region global
  const [customer] = r.useCustomer(customerID)
  const totalCost = r.useTotalCost('customer', customerID, true)
  const { currProducts } = r.useProducts(customerID)
  const { appliedLoan, appliedLoanUnlock } = r.useLoanOpts(customerID)

  const { rows: _secCamRows } = r.useRowable<c.CamGroup>(
    customerID,
    'security_cams_detail',
  )
  const secCamRows =
    customer.security_cams === 'no'
      ? (c.EMPTY_ARRAY as c.CamGroups)
      : _secCamRows.filter((cg) => !!cg.qty && !!cg.type)
  const { rows: _secSysRows } = r.useRowable<c.SecSysGroup>(
    customerID,
    'security_system_detail',
  )
  const secSysRows =
    customer.security_system === 'no'
      ? (c.EMPTY_ARRAY as c.SecSysGroups)
      : _secSysRows.filter((ssg) => !!ssg.qty && !!ssg.type)
  const { rows: _secTVRows } = r.useRowable<c.SecurityTV>(
    customerID,
    'security_tv_detail',
  )
  const secTVRows =
    customer.security_tv === 'no'
      ? (c.EMPTY_ARRAY as c.SecurityTVs)
      : _secTVRows.filter((ssg) => !!ssg.type)
  //#endregion global
  //#region local
  const [closerCalcOpen, , toggleCloserCalc] = r.useBool(false)
  const [, , payMethodToggle] = r.useSessionVal('payMethodOpen')

  const [collapsed, collapsedSet] = React.useState<c.DictS<boolean>>(c.emptyObj)

  const collapsedHandleMin = React.useMemo<c.DictS<() => void>>(
    () =>
      c.mapValues(c.efficiencyToInputs, (_, efficiencyKey) => () => {
        collapsedSet((_) => ({ ..._, [efficiencyKey]: true }))
      }),
    [],
  )

  const collapsedHandleMax = React.useMemo<c.DictS<() => void>>(
    () =>
      c.mapValues(c.efficiencyToInputs, (_, efficiencyKey) => () => {
        collapsedSet((_) => ({ ..._, [efficiencyKey]: false }))
      }),
    [],
  )

  React.useEffect(() => {
    collapsedSet((currCollapsed) =>
      c.mapValues(c.efficiencyToInputs, (_, efficiencyKey) =>
        // If an efficiency is toggled off, collapse it, else leave as is
        customer[(efficiencyKey + '_show') as c.CustomerField] === 0
          ? true
          : !!currCollapsed[efficiencyKey],
      ),
    )
  }, [customer])

  const toggledHandleHandleOn = React.useMemo<c.DictS<() => void>>(
    () =>
      c.mapValues(c.efficiencyToInputs, (_, efficiencyKey) => () => {
        c.updateCustomer(customerID, { [efficiencyKey + '_show']: 0 })
      }),
    [customerID],
  )
  const toggledHandleHandleOff = React.useMemo<c.DictS<() => void>>(
    () =>
      c.mapValues(c.efficiencyToInputs, (_, efficiencyKey) => () => {
        c.updateCustomer(customerID, { [efficiencyKey + '_show']: 1 })
      }),
    [customerID],
  )

  const [editingEfficiencyVal, editingEfficiencySet] = React.useState<
    c.CustomerField | ''
  >('')
  const editingEfficiencyHandle = React.useMemo(
    () =>
      c.mapValues(c.efficiencyToInputs, (_, efficiencyKey) => () => {
        editingEfficiencySet(efficiencyKey)
      }),
    [],
  )
  const editingEfficiencyCloseHandle = React.useCallback(() => {
    editingEfficiencySet('')
  }, [])

  const turnOffEfficiencyHandle = React.useMemo(
    () =>
      c.mapValues(c.efficiencyToInputs, (_, efficiencyKey) => () => {
        if (
          window.confirm(
            `Are you sure you want to turn off "${c.getFieldLabel(
              efficiencyKey,
            )}"?`,
          )
        ) {
          c.updateCustomer(customerID, {
            [efficiencyKey]: 'no',
          })
        }
      }),
    [customerID],
  )
  //#endregion local
  //#region derived
  type JobSummaryItem = c.r<{
    id: string
    name: string
    notes: string[]
    price: number
    qty: number
  }>
  const items = React.useMemo((): readonly JobSummaryItem[] => {
    const customItems = c
      .getFilledCustomFields(customer)
      .map((customField): JobSummaryItem => {
        const qty = customer[
          (customField + '_qty') as c.CustomerField
        ] as number

        const customerCost = customer[
          (customField + '_customer_cost') as c.CustomerField
        ] as string

        const notes = (
          customer[(customField + '_notes') as c.CustomerField] as string
        ).trim()

        return {
          id: customField,
          name: customer[(customField + '_name') as c.CustomerField] as string,
          notes: notes.length === 0 ? [] : notes.split('\n'),
          price: c.parseFloating(customerCost) / qty,
          qty: qty,
        }
      })

    const nonSecurityActiveEfficiencies = c.efficiencyKeys
      .filter((k) => !k.startsWith('security_'))
      .filter((k) => !k.startsWith('custom_'))
      .filter((k) => customer[k] === 'yes')
      .map((field): JobSummaryItem => {
        const notes = (
          customer[(field + '_notes') as c.CustomerField] as string
        ).trim()

        const extraNotes =
          (
            customer[(field + 'extra_notes') as c.CustomerField] as string
          )?.trim() || ''

        const notesLines = notes.length === 0 ? [] : notes.split('\n')
        const extraNotesLines =
          extraNotes.length === 0 ? [] : extraNotes.split('\n')

        const qty = customer[(field + '_qty') as c.CustomerField] as number

        return {
          id: field,
          name: c.getFieldLabel(field),
          notes: [...notesLines, ...extraNotesLines],
          price: c.parseFloating(
            customer[(field + '_customer_cost') as c.CustomerField],
          ),
          qty: qty || -1,
        }
      })

    return [
      //#region securityCamsItems
      ...(customer.security_cams === 'no' || secCamRows.length === 0
        ? []
        : [
            {
              id: 'security_cams',
              name: collapsed['security_cams']
                ? c.getFieldLabel('security_cams')
                : '--------------- Security Camera ---------------',
              notes: [],
              price: collapsed['security_cams']
                ? c.parseFloating(customer.security_cams_customer_cost)
                : -1,
              qty: -1,
            },
          ]),
      ...(customer.security_cams === 'no' || collapsed['security_cams']
        ? []
        : secCamRows
      ).map(
        ({ id, notes, price, qty, type }): JobSummaryItem => ({
          id,
          name: type,
          notes: [notes],
          price,
          qty,
        }),
      ),
      ...(customer.security_cams === 'no' ||
      secCamRows.length === 0 ||
      customer.security_cams_extra_notes.trim().length === 0 ||
      collapsed['security_cams']
        ? []
        : [
            {
              id: 'security_cams_extra_notes',
              name: dashes,
              notes: customer.security_cams_extra_notes.trim().split('\n'),
              price: -1,
              qty: -1,
            },
            {
              id: '/security_cams_extra_notes',
              name: dashes,
              notes: [],
              price: -1,
              qty: -1,
            },
          ]),
      //#endregion securityCamsItems
      //#region securitySystemItems
      ...(customer.security_system === 'no' || secSysRows.length === 0
        ? []
        : [
            {
              id: 'security_system',
              name: collapsed['security_system']
                ? c.getFieldLabel('security_system')
                : '--------------- Security System ---------------',
              notes: [],
              price: collapsed['security_system']
                ? c.parseFloating(customer.security_system_customer_cost)
                : -1,
              qty: -1,
            },
          ]),
      ...(customer.security_system === 'no' || collapsed['security_system']
        ? []
        : secSysRows
      ).map(
        ({ id, notes, price, qty, type }): JobSummaryItem => ({
          id,
          name: type,
          notes: [notes],
          price,
          qty,
        }),
      ),
      ...(customer.security_system === 'no' ||
      secSysRows.length === 0 ||
      customer.security_system_extra_notes.trim().length === 0 ||
      collapsed['security_system']
        ? []
        : [
            {
              id: 'security_system_extra_notes',
              name: dashes,
              notes: customer.security_system_extra_notes.trim().split('\n'),
              price: -1,
              qty: -1,
            },
            {
              id: '/security_system_extra_notes',
              name: dashes,
              notes: [],
              price: -1,
              qty: -1,
            },
          ]),
      //#endregion securitySystemItems
      ...customItems,
      ...nonSecurityActiveEfficiencies,
      //#region securityTvItems
      ...(customer.security_tv === 'no' || secTVRows.length === 0
        ? []
        : [
            {
              id: 'security_tv',
              name: collapsed['security_tv']
                ? c.getFieldLabel('security_tv')
                : '--------------------- TVs ---------------------',
              notes: [],
              price: collapsed['security_tv']
                ? c.parseFloating(customer.security_tv_customer_cost)
                : -1,
              qty: -1,
            },
          ]),
      ...(customer.security_tv === 'no' || collapsed['security_tv']
        ? []
        : secTVRows
      ).map(
        ({ id, price, qty, type }): JobSummaryItem => ({
          id,
          name: type,
          notes: [],
          price,
          qty,
        }),
      ),
      ...(customer.security_tv === 'no' ||
      secTVRows.length === 0 ||
      customer.security_tv_extra_notes.trim().length === 0 ||
      collapsed['security_tv']
        ? []
        : [
            {
              id: 'security_tv_extra_notes',
              name: dashes,
              notes: customer.security_tv_extra_notes.trim().split('\n'),
              price: -1,
              qty: -1,
            },
            {
              id: '/security_tv_extra_notes',
              name: dashes,
              notes: [],
              price: -1,
              qty: -1,
            },
          ]),
      //#endregion securityTvItems
    ]
  }, [collapsed, customer, secCamRows, secSysRows, secTVRows])
  //#endregion derived

  return (
    <>
      <ReportPages
        companyBorder
        customerID={customerID}
        hPad
        noPDF={noPDF}
        title="Job Summary"
      >
        {!isPDF && (
          <div
            style={{
              ...gs.absolute,
              ...gs.rowCentered,
              gap: 12,
              right: horizontalPad,
              top: 48,
            }}
          >
            {(currProducts.includes('Solar') || currProducts.length === 0) && (
              <Feather
                color={iconColor}
                name="sun"
                onPress={toggleCloserCalc}
                size={headerIconSize}
                style={gs.userSelectNone}
              />
            )}

            {(currProducts.includes('Security') ||
              currProducts.length === 0) && (
              <>
                <Feather
                  color={iconColor}
                  name="trello"
                  onPress={payMethodToggle}
                  size={headerIconSize}
                  style={gs.userSelectNone}
                />

                <Feather
                  color={appliedLoan ? iconColor : nearWhite}
                  name="unlock"
                  onPress={appliedLoan ? appliedLoanUnlock : undefined}
                  size={headerIconSize}
                  style={gs.userSelectNone}
                />
              </>
            )}
          </div>
        )}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            fontFamily,
            fontSize,
            margin: 0,
            width: '100%',
          }}
        >
          {!isPDF && (
            <p
              style={{
                fontFamily,
                fontSize,
                fontWeight: 'bold',
                textAlign: 'center',
                width: columnWidths[0],
              }}
            />
          )}

          <p
            style={{
              fontFamily,
              fontSize,
              fontWeight: 'bold',
              textAlign: 'center',
              width: columnWidths[1],
            }}
          >
            Qty
          </p>

          <p
            style={{
              fontFamily,
              fontSize,
              fontWeight: 'bold',
              width: isPDF
                ? parseFloat(columnWidths[0]) +
                  parseFloat(columnWidths[2]) +
                  '%'
                : columnWidths[2],
            }}
          >
            Description
          </p>

          <p
            style={{
              fontFamily,
              fontSize,
              fontWeight: 'bold',
              textAlign: 'end',
              width: columnWidths[3],
            }}
          >
            Unit Cost
          </p>

          <p
            style={{
              fontFamily,
              fontSize,
              fontWeight: 'bold',
              textAlign: 'end',
              width: columnWidths[4],
            }}
          >
            Total Cost
          </p>
        </div>

        {items.flatMap(({ id, name, notes, price, qty }) => {
          const showNotes = (isPDF || !collapsed[id]) && notes.length > 0
          return [
            <div
              key={id}
              style={{
                ...gs.flowRootWidth100,
                margin: 0,
                marginBottom: showNotes ? 0 : 12,
                width: '100%',
              }}
            >
              {!isPDF && (
                <div
                  style={{
                    ...gs.rowWrap,
                    float: 'left',
                    width: columnWidths[0],
                  }}
                >
                  {false && (
                    <Feather
                      color={
                        c.efficiencyToInputs[id as c.CustomerField]
                          ? iconColor
                          : 'transparent'
                      }
                      name={
                        customer[(id + '_show') as c.CustomerField] === 1
                          ? 'check-square'
                          : 'square'
                      }
                      onPress={
                        c.efficiencyToInputs[id as c.CustomerField]
                          ? customer[(id + '_show') as c.CustomerField] === 1
                            ? toggledHandleHandleOn[id]
                            : toggledHandleHandleOff[id]
                          : undefined
                      }
                      size={iconSize}
                      style={iconStyle}
                    />
                  )}

                  <Feather
                    color={
                      c.efficiencyToInputs[id as c.CustomerField]
                        ? iconColor
                        : 'transparent'
                    }
                    name="edit-2"
                    onPress={
                      c.efficiencyToInputs[id as c.CustomerField]
                        ? editingEfficiencyHandle[id as c.CustomerField]
                        : undefined
                    }
                    size={iconSize}
                    style={iconStyle}
                  />

                  {false && (
                    <Feather
                      color={
                        c.efficiencyToInputs[id as c.CustomerField]
                          ? iconColor
                          : 'transparent'
                      }
                      name="x"
                      onPress={
                        c.efficiencyToInputs[id as c.CustomerField]
                          ? turnOffEfficiencyHandle[id as c.CustomerField]
                          : undefined
                      }
                      size={iconSize}
                      style={iconStyle}
                    />
                  )}

                  <Feather
                    color={
                      c.efficiencyToInputs[id as c.CustomerField]
                        ? customer[(id + '_show') as c.CustomerField] === 0 ||
                          (notes.length === 0 && !id.startsWith('security_'))
                          ? nearWhite
                          : iconColor
                        : 'transparent'
                    }
                    name={
                      collapsed[id] ||
                      customer[(id + '_show') as c.CustomerField] === 0 ||
                      (notes.length === 0 && !id.startsWith('security_'))
                        ? 'maximize-2'
                        : 'minimize-2'
                    }
                    onPress={
                      c.efficiencyToInputs[id as c.CustomerField] &&
                      !!customer[(id + '_show') as c.CustomerField]
                        ? collapsed[id]
                          ? collapsedHandleMax[id]
                          : collapsedHandleMin[id]
                        : undefined
                    }
                    size={iconSize}
                    style={iconStyle}
                  />
                </div>
              )}

              <p
                style={{
                  color:
                    customer[(id + '_show') as c.CustomerField] === 0
                      ? nearWhite
                      : '#000000',
                  float: 'left',
                  fontFamily,
                  fontSize,
                  margin: 0,
                  textAlign: 'center',
                  width: columnWidths[1],
                }}
              >
                {qty === -1 ? String.fromCharCode(160) : qty}
              </p>

              <div
                style={{
                  ...gs.column,
                  margin: 0,
                  float: 'left',
                  fontFamily,
                  fontSize,
                  width: isPDF
                    ? parseFloat(columnWidths[0]) +
                      parseFloat(columnWidths[2]) +
                      '%'
                    : columnWidths[2],
                }}
              >
                <p
                  style={{
                    color:
                      customer[(id + '_show') as c.CustomerField] === 0
                        ? nearWhite
                        : '#000000',
                    float: 'left',
                    fontFamily,
                    fontSize,
                    margin: 0,
                  }}
                >
                  {name}
                </p>
              </div>

              <p
                style={{
                  color:
                    customer[(id + '_show') as c.CustomerField] === 0
                      ? nearWhite
                      : '#000000',
                  margin: 0,
                  float: 'left',
                  fontFamily,
                  fontSize,
                  textAlign: 'end',
                  width: columnWidths[3],
                }}
              >
                {price === -1 || qty === -1
                  ? '-'
                  : Intl.NumberFormat('en-US', {
                      currency: 'USD',
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                      style: 'currency',
                    }).format(price)}
              </p>

              <p
                style={{
                  color:
                    customer[(id + '_show') as c.CustomerField] === 0
                      ? nearWhite
                      : '#000000',
                  float: 'left',
                  fontFamily,
                  fontSize,
                  margin: 0,
                  textAlign: 'end',
                  width: columnWidths[4],
                }}
              >
                {price === -1
                  ? '-'
                  : Intl.NumberFormat('en-US', {
                      currency: 'USD',
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                      style: 'currency',
                      // Math.abs() in case qty is -1 (flag for non-quantifiable)
                    }).format(price * Math.abs(qty))}
              </p>
            </div>,
            ...(showNotes
              ? notes.map((noteLine, i) => (
                  <p
                    key={`${id}-note-${i}`}
                    style={{
                      alignSelf: 'flex-start',
                      margin: 0,
                      marginTop: i === 0 ? 2 : 0,
                      marginBottom: i === notes.length - 1 ? 12 : 0,
                      marginLeft: isPDF
                        ? `calc(${columnWidths[1]} + 3ch)`
                        : `calc(${columnWidths[0]} + ${columnWidths[1]} + 3ch)`,
                      width: isPDF
                        ? `calc(${
                            parseFloat(columnWidths[0]) +
                            parseFloat(columnWidths[2])
                          }% - 3ch)`
                        : `calc(${columnWidths[2]} - 3ch)`,
                      fontFamily,
                      fontSize,
                    }}
                  >
                    {noteLine}
                  </p>
                ))
              : []),
          ]
        })}

        <div
          style={{
            ...gs.flowRootWidth100,
            display: 'flex',
            flexDirection: 'column',
            float: 'left',
            margin: 0,
            marginBottom: 12,
            width: '100%',
          }}
        >
          <p
            style={{
              alignSelf: 'flex-end',
              borderTopColor: '#000000',
              borderTopStyle: 'solid',
              borderTopWidth: 1,
              fontFamily,
              fontSize: 16,
              fontWeight: 'bold',
              margin: 0,
              textAlign: 'end',
            }}
          >
            {'GT: ' +
              Intl.NumberFormat('en-US', {
                currency: 'USD',
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
                style: 'currency',
                // Math.abs() in case qty is -1 (flag for non-quantifiable)
              }).format(totalCost)}
          </p>
        </div>

        <div style={gs.flowRootWidth100}>
          <p
            style={{
              alignSelf: 'flex-start',
              fontFamily,
              fontSize,
              margin: 0,
              marginBottom: 4,
              marginTop: 4,
              // textAlign: 'end',
            }}
          >
            Year home was built: {customer.yearHomeBuilt}
          </p>
          <p
            style={{
              alignSelf: 'flex-start',
              fontFamily,
              fontSize,
              margin: 0,
              marginBottom: 4,
              marginTop: 4,
              // textAlign: 'end',
            }}
          >
            Attic square footage: {customer.attic_square_footage}
          </p>
          <p
            style={{
              alignSelf: 'flex-start',
              fontFamily,
              fontSize,
              margin: 0,
              marginBottom: 4,
              marginTop: 4,
              // textAlign: 'end',
            }}
          >
            Shingles or tiles: {customer.roof_shingles_or_tiles}
          </p>
          <p
            style={{
              alignSelf: 'flex-start',
              fontFamily,
              fontSize,
              margin: 0,
              marginBottom: 4,
              marginTop: 4,
              // textAlign: 'end',
            }}
          >
            One or two story building: {customer.homeStoryNumber}
          </p>
          <p
            style={{
              alignSelf: 'flex-start',
              fontFamily,
              fontSize,
              margin: 0,
              marginBottom: 4,
              marginTop: 4,
              // textAlign: 'end',
            }}
          >
            Building type: {customer.buildingType}
          </p>
        </div>

        {!!customer.globalNotes.trim() && (
          <div style={gs.flowRootWidth100}>
            {customer.globalNotes
              .trim()
              .split('\n')
              .map((noteLine, i) => (
                <p
                  key={i}
                  style={{
                    alignSelf: 'flex-start',
                    fontFamily,
                    fontSize,
                    margin: 0,
                    marginBottom: 4,
                    marginTop: 4,
                    // textAlign: 'end',
                  }}
                >
                  {noteLine}
                </p>
              ))}
          </div>
        )}
      </ReportPages>

      <ThemeProvider theme={innerTheme}>
        <cp.Dialog
          noCanvas
          onClose={editingEfficiencyCloseHandle}
          open={!!editingEfficiencyVal}
        >
          {!!editingEfficiencyVal && (
            <cpm.CheckGroup
              alwaysOn
              as="canvas"
              customerID={customerID}
              field={editingEfficiencyVal as c.CustomerField}
            />
          )}
        </cp.Dialog>
      </ThemeProvider>

      <ThemeProvider theme={innerTheme}>
        <cp.Dialog open={closerCalcOpen} onClose={toggleCloserCalc}>
          <cpm.CloserCalc customerID={customerID} deductDisabled />
        </cp.Dialog>
      </ThemeProvider>
    </>
  )
})

const columnWidths = ['8%', '8%', '60%', '12%', '12%'] as const

const dashes = '-----------------------------------------------'

const nearWhite = '#DDDDDD'

const iconColor = '#777777'
const iconSize = 14
const iconStyle = { ...gs.userSelectNone, margin: 4 }

const fontFamily = 'Monospace'
const fontSize = 11

const headerIconSize = 20
